import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getAllPosts } from './posts';
import './blog.css';

const LOGO = `${process.env.PUBLIC_URL}/g2-logo.png`;

// ─────────────────────────────────────────────────────────────────
// Shared chrome
// ─────────────────────────────────────────────────────────────────
function BlogNav() {
  return (
    <header className="bl-nav">
      <div className="bl-wrap bl-nav-inner">
        <a className="bl-nav-brand" href="/" aria-label="Groupardy home">
          <img src={LOGO} alt="Groupardy logo" className="bl-nav-logo" />
          <span className="bl-nav-wordmark">Groupardy</span>
        </a>
        <nav className="bl-nav-links" aria-label="Primary">
          <a className="bl-nav-link" href="/#how">How it works</a>
          <a className="bl-nav-link" href="/#host">AI host</a>
          <Link className="bl-nav-link bl-nav-link-active" to="/blog">Blog</Link>
          <a className="bl-nav-link" href="/#faq">FAQ</a>
        </nav>
        <div className="bl-nav-actions">
          <a className="bl-nav-signin" href="/login">Sign in</a>
          <a className="bl-btn bl-btn-primary bl-btn-sm" href="/create">
            Start a game
          </a>
        </div>
      </div>
    </header>
  );
}

function BlogFooter() {
  return (
    <footer className="bl-footer">
      <div className="bl-wrap">
        <div className="bl-foot-grid">
          <div className="bl-foot-brand">
            <div className="bl-wm">
              <img src={LOGO} alt="" style={{ width: 28, height: 28 }} />
              Groupardy
            </div>
            <p>The AI-powered Jeopardy-style game show for groups. A real game show for the room you're already in.</p>
            <div className="bl-foot-socials">
              <a href="https://www.instagram.com/groupardy/" target="_blank" rel="noopener noreferrer" aria-label="Instagram">📷</a>
              <a href="https://x.com/groupardy" target="_blank" rel="noopener noreferrer" aria-label="X (Twitter)">𝕏</a>
            </div>
          </div>
          <div className="bl-foot-col">
            <h5>Play</h5>
            <ul>
              <li><a href="/create">Start a game</a></li>
            </ul>
          </div>
          <div className="bl-foot-col">
            <h5>Product</h5>
            <ul>
              <li><a href="/#how">How it works</a></li>
              <li><a href="/#host">The AI host</a></li>
            </ul>
          </div>
          <div className="bl-foot-col">
            <h5>Company</h5>
            <ul>
              <li><a href="/about">About</a></li>
              <li><Link to="/blog">Blog</Link></li>
              <li><a href="/press">Press</a></li>
              <li><a href="/contact">Contact</a></li>
            </ul>
          </div>
          <div className="bl-foot-col">
            <h5>Support</h5>
            <ul>
              <li><a href="/help">Help center</a></li>
              <li><a href="/#faq">FAQ</a></li>
              <li><a href="/terms">Terms</a></li>
              <li><a href="/privacy">Privacy</a></li>
            </ul>
          </div>
        </div>
        <div className="bl-foot-bar">
          <div>© 2026 Groupardy. All rights reserved.</div>
          <div>Made with ❤️ for the group chat that always says "we should hang out more".</div>
        </div>
      </div>
    </footer>
  );
}

// Exported so BlogPost can reuse without dropping into shared layout components.
export { BlogNav, BlogFooter };

// ─────────────────────────────────────────────────────────────────
// Date formatter
// ─────────────────────────────────────────────────────────────────
const formatDate = (iso) => {
  // iso is "YYYY-MM-DD" — render in a stable format without timezone surprises
  const [y, m, d] = iso.split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });
};

// ─────────────────────────────────────────────────────────────────
// JSON-LD
// ─────────────────────────────────────────────────────────────────
const buildBlogLd = (posts) =>
  JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'Blog',
    name: 'The Groupardy Blog',
    url: 'https://groupardy.com/blog',
    description:
      'Notes on building Groupardy — the AI-powered Jeopardy-style game show for groups. Patterns, posts, and the occasional category that was too good not to publish.',
    publisher: {
      '@type': 'Organization',
      name: 'Groupardy',
      url: 'https://groupardy.com/',
      logo: 'https://groupardy.com/g2-logo.png',
    },
    blogPost: posts.map((p) => ({
      '@type': 'BlogPosting',
      headline: p.title,
      datePublished: p.date,
      author: { '@type': 'Person', name: p.author },
      url: `https://groupardy.com/blog/${p.slug}`,
      description: p.excerpt,
    })),
  });

// ─────────────────────────────────────────────────────────────────
// Page
// ─────────────────────────────────────────────────────────────────
const Blog = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const posts = getAllPosts();

  return (
    <div className="blog">
      <Helmet>
        <title>Blog · Groupardy</title>
        <meta
          name="description"
          content="Notes from the team building Groupardy — the AI-powered Jeopardy-style game show for groups. Posts on the host, the boards, and what makes trivia night actually work."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <link rel="canonical" href="https://groupardy.com/blog" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://groupardy.com/blog" />
        <meta property="og:title" content="The Groupardy Blog" />
        <meta
          property="og:description"
          content="Notes from the team building Groupardy. Posts on the AI host, the boards, and what makes trivia night actually work."
        />
        <meta property="og:site_name" content="Groupardy" />
        <meta property="og:image" content="https://groupardy.com/og-image.jpg" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="The Groupardy Blog" />
        <meta
          name="twitter:description"
          content="Notes from the team building Groupardy. The AI host, the boards, and what makes trivia night actually work."
        />
        <meta name="twitter:image" content="https://groupardy.com/og-image.jpg" />
        <meta name="theme-color" content="#2ec4b6" />
        <script type="application/ld+json">{buildBlogLd(posts)}</script>
      </Helmet>

      <BlogNav />

      <main>
        <section className="bl-page-header">
          <div className="bl-wrap bl-page-header-inner">
            <div className="bl-eyebrow">The Groupardy Blog</div>
            <h1>Notes from the room.</h1>
            <p>
              Posts on what we're building, patterns that make a game night land,
              and the occasional category that was too good not to publish.
            </p>
          </div>
        </section>

        <section className="bl-index">
          <div className="bl-wrap bl-index-inner">
            <ul className="bl-post-list">
              {posts.map((post) => (
                <li key={post.slug}>
                  <Link to={`/blog/${post.slug}`} className="bl-post-card">
                    <div className="bl-post-meta">
                      <span>{formatDate(post.date)}</span>
                      <span className="bl-dot" />
                      <span>{post.author}</span>
                      {post.readMinutes && (
                        <>
                          <span className="bl-dot" />
                          <span>{post.readMinutes} min read</span>
                        </>
                      )}
                    </div>
                    <h2 className="bl-post-title">{post.title}</h2>
                    <p className="bl-post-excerpt">{post.excerpt}</p>
                    <span className="bl-post-readlink">Read post</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>
      </main>

      <BlogFooter />
    </div>
  );
};

export default Blog;
